import { User, FileText, Clock, MessageSquare, Calendar, Copy, Check, Gauge } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import QualityBadge from '@/components/ui/QualityBadge';
import QualityOverrideDialog from './QualityOverrideDialog';
import { formatMessageTimestamp } from '@/utils/dateFormat';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import Markdown from '@/components/markdown/Markdown';
import truncateText from '@/utils/truncateText';
import useTranslation from '@/hooks/useTranslation';
import { AllTranscriptsProps } from '@/types';
import {
  HIDDEN_METADATA_KEYS,
  formatMetadataCellValue,
  isRenderableMetadataValue,
} from '@/utils/metadataDisplay';
import ConversationCategories from './ConversationCategories';

interface ConversationMetadataProps {
  conversation?: AllTranscriptsProps | null;
  totalMessages?: number;
  isExpanded?: boolean;
  onSummaryToggle?: (expanded: boolean) => void;
  maxSummaryWords?: number;
  className?: string;
}

const statusColors: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  abandoned: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  screening_failed: 'bg-red-50 text-red-700 border-red-200',
};

function ConversationIdRow({ id }: { id?: string }) {
  const [copied, setCopied] = useState(false);

  if (!id) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS contexts
    }
  };

  return (
    <div className="border-t border-neutral-100 mt-4 pt-3">
      <button
        type="button"
        onClick={handleCopy}
        className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 transition-colors group w-full"
        title="Copy conversation ID"
      >
        <span className="truncate">{id}</span>
        {copied ? (
          <Check className="h-3 w-3 text-primary shrink-0" />
        ) : (
          <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        )}
      </button>
    </div>
  );
}

export default function ConversationMetadata({
  conversation,
  totalMessages,
  isExpanded = false,
  onSummaryToggle,
  maxSummaryWords = 80,
  className = '',
}: ConversationMetadataProps) {
  const { lang } = useTranslation();

  const summary = conversation?.digest?.summary ?? '';
  const status = conversation?.status ?? '';
  const statusStyle = statusColors[status] || 'bg-neutral-50 text-neutral-700 border-neutral-200';

  const hasDate = !!conversation?.createdAt;
  const hasDuration = !!(conversation?.startedAt && conversation?.finishedAt);

  return (
    <Card
      className={`border-neutral-200/80 shadow-sm rounded-xl w-full max-w-[400px] md800:max-w-none overflow-y-auto p-5 shrink-0 ${className}`}
    >
      {/* Header: Participant + Status */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-neutral-400 shrink-0" />
          <h3 className="text-sm font-semibold text-neutral-900">
            {lang.get('msg.participant')} #{conversation?.respondent_index}
          </h3>
        </div>
        {status && (
          <span
            className={`inline-flex items-center self-start px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border ${statusStyle}`}
          >
            {status.replace('_', ' ')}
          </span>
        )}
      </div>

      {/* Stats — compact key/value rows */}
      <div className="border-t border-neutral-100 mt-4 pt-4 flex flex-col gap-2.5">
        {hasDate && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
            <span className="text-neutral-500">{lang.get('msg.createdAt')}</span>
            <span className="ml-auto font-medium text-neutral-700">
              {formatMessageTimestamp(conversation?.createdAt as string)}
            </span>
          </div>
        )}
        {hasDuration && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
            <span className="text-neutral-500">{lang.get('msg.duration')}</span>
            <span className="ml-auto font-medium text-neutral-700">
              {calculateConversationDuration({
                startedAt: conversation?.startedAt as string,
                finishedAt: conversation?.finishedAt as string,
              })}
            </span>
          </div>
        )}
        {typeof totalMessages === 'number' && totalMessages > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <MessageSquare className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
            <span className="text-neutral-500">{lang.get('msg.totalMessages')}</span>
            <span className="ml-auto font-medium text-neutral-700">{totalMessages}</span>
          </div>
        )}
        {/* TELV1-433 — judged quality category; reason on hover; researcher
            override via the pencil (category-only, reason required). */}
        {conversation?.quality?.status === 'complete' && (
          <div className="flex items-center gap-2 text-sm">
            <Gauge className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
            <span className="text-neutral-500">{lang.get('msg.quality')}</span>
            <span className="ml-auto flex items-center gap-1">
              <QualityBadge quality={conversation.quality} />
              <QualityOverrideDialog conversation={conversation} />
            </span>
          </div>
        )}
      </div>

      {/* Screening / Metadata (stacked vertically) */}
      {conversation?.metadata && Object.keys(conversation.metadata).length > 0 && (
        <div className="border-t border-neutral-100 mt-4 pt-4 flex flex-col gap-3">
          {Object.entries(conversation.metadata)
            .filter(
              ([key, value]) => !HIDDEN_METADATA_KEYS.has(key) && isRenderableMetadataValue(value),
            )
            .map(([key, value]) => (
              <div key={key} className="flex flex-col gap-0.5">
                <span className="text-sm text-neutral-500">{key}</span>
                <span className="text-sm font-medium text-neutral-900">
                  {formatMetadataCellValue(value)}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="border-t border-neutral-100 mt-4 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-neutral-400" />
            <span className="text-sm font-semibold text-neutral-900">
              {lang.get('msg.summary')}
            </span>
          </div>
          <div className="text-sm text-neutral-600">
            <Markdown>
              {isExpanded ? summary : truncateText(summary as string, maxSummaryWords)}
            </Markdown>
            {(summary ?? '').split(' ').length > maxSummaryWords && onSummaryToggle && (
              <button
                type="button"
                className="ml-1 text-sm text-primary hover:text-darker-primary font-medium"
                onClick={() => onSummaryToggle(!isExpanded)}
              >
                {isExpanded ? lang.get('msg.showLess') : lang.get('msg.showMore')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Categories */}
      <ConversationCategories quantification={conversation?.quantification} />

      {/* Conversation ID */}
      <ConversationIdRow id={conversation?.conversation_id ?? conversation?._id} />
    </Card>
  );
}
